import React, { useState, useMemo } from 'react';
import { aggregateMonthlyExpenses, BUCKET_LABELS } from '../utils/expenseCategoryMap';
import { useIsMobile } from '../useIsMobile';
import { isAdmin } from './AdminDashboard';
import { monthSummary, ACCTS, ACCT_LABEL } from '../utils/receiving';
import { menuClassMap, monthRevenueMix } from '../utils/salesMix';   // V166 음식 · 주류·음료 매출
import { investmentsOf, depForMonth, startMonthOf, paybackSummary, addMonths, monthDiff, investTotals } from '../utils/investment';   // V170 투자비·감가상각
import InvestmentPanel from './InvestmentPanel';
import LaborDetail, { laborSummary } from './LaborDetail';   // V171 인건비 사람별
import { L, LH, M, MU, ym, mo, trLang } from '../i18n/L';   // V160 중국어   // V158 입고 → 식재료비 · V159 계정과목

// ── 유틸
const fmt = (n) => (n ? Math.round(n).toLocaleString('ko-KR') : '0');
const fmtWon = (n) => M(n);   // V160 매장 통화 (중국 매장은 ¥)

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthList = () => {
  const months = [];
  const now = new Date();
  // 24개월 — 옛 데이터(POS 엑셀, 빠른 매출/비용 입력 등) 지원
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
};

// ── 숫자 입력 컴포넌트
function MoneyInput({ value, onChange, placeholder = '0' }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type="number"
      min="0"
      value={value ? String(Math.round(Number(value) * 1000) / 1000) : ''}   /* V170 소수 셋째 자리까지 (1682.1000000000001 → 1682.1) */
      placeholder={placeholder}
      onChange={e => onChange(Number(e.target.value) || 0)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb',
        borderRadius: '8px', fontSize: '14px', textAlign: 'right',
        outline: 'none', transition: 'border-color 0.15s',
        borderColor: focused ? '#FF6F0F' : '#e5e7eb',
      }}
    />
  );
}

// ── 섹션 카드
function SectionCard({ title, icon, children, color = '#FF6F0F' }) {
  return (
    <div style={{
      background: '#fff', borderRadius: '14px', overflow: 'hidden',
      boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: '14px',
    }}>
      <div style={{
        padding: '14px 18px', borderBottom: '1px solid #f0f0f0',
        display: 'flex', alignItems: 'center', gap: '8px',
      }}>
        <span style={{ fontWeight: '800', fontSize: '15px', color }}>{title}</span>
      </div>
      <div style={{ padding: '16px 18px' }}>{children}</div>
    </div>
  );
}

// ── 입력 행
function InputRow({ label, value, onChange, autoFill, autoLabel = L('발주↔연동'), autoTitle = L('발주서에서 자동 계산') }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
      <span style={{ width: '80px', fontSize: '13px', color: '#555', flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1 }}>
        <MoneyInput value={value} onChange={onChange} />
      </div>
      {autoFill && (
        <button
          onClick={autoFill}
          title={autoTitle}
          style={{
            padding: '8px 10px', borderRadius: '7px', border: '1.5px solid #FF6F0F',
            background: '#fff', color: '#18181B', fontSize: '11px', fontWeight: '700',
            cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
          }}>
          {autoLabel}
        </button>
      )}
    </div>
  );
}

// ── 요약 카드
function SummaryCard({ label, value, sub, color, big }) {
  return (
    <div style={{
      background: color || '#fff', borderRadius: '12px', padding: '14px 16px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.07)', flex: 1, minWidth: '120px',
    }}>
      <div style={{ fontSize: '11px', color: color ? 'rgba(255,255,255,0.75)' : '#9ca3af', marginBottom: '4px' }}>{label}</div>
      <div style={{
        fontSize: big ? '20px' : '17px', fontWeight: '900',
        color: color ? '#fff' : '#1a1a1a', lineHeight: 1.1,
      }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', marginTop: '3px', color: color ? 'rgba(255,255,255,0.7)' : '#9ca3af' }}>{sub}</div>}
    </div>
  );
}

// ── 비용 도넛 차트 (SVG)
function CostDonut({ costs: base, extra = [] }) {
  // V160: 화면에 보이는 값(장부·입고 자동 포함) + 입고 계정과목 줄(연료·수선 등)도 같이
  const costs = { ...base };
  for (const r of extra) costs['acct_' + r.acct] = r.amount;
  const labels = [
    { key: 'ingredients', name: L('식재료'), color: '#18181B' },
    { key: 'labor',       name: L('인건비'),  color: '#1A56A0' },
    { key: 'rent',        name: L('임대료'),  color: '#EA580C' },
    { key: 'utilities',   name: L('공과금'),  color: '#7C3AED' },
    { key: 'other',       name: L('기타'),    color: '#6b7280' },
    ...extra.map(r => ({ key: 'acct_' + r.acct, name: r.label, color: r.acct === 'depreciation' ? '#0F766E' : (ACCT_LABEL[r.acct]?.color || '#9ca3af') })),
  ];
  const total = labels.reduce((s, l) => s + (costs[l.key] || 0), 0);
  if (total === 0) return (
    <div style={{ textAlign: 'center', color: '#bbb', padding: '24px', fontSize: '13px' }}>
      {L("비용을 입력하면 구성 비율이 보여요")}
    </div>
  );

  const cx = 70, cy = 70, r = 50, stroke = 22;
  const circumference = 2 * Math.PI * r;
  let offset = 0;
  const slices = labels.map(l => {
    const pct = (costs[l.key] || 0) / total;
    const slice = { ...l, pct, dasharray: pct * circumference, dashoffset: -offset * circumference };
    offset += pct;
    return slice;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <svg width="140" height="140" viewBox="0 0 140 140">
        {slices.map((s, i) => (
          <circle key={i} cx={cx} cy={cy} r={r}
            fill="none" stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${s.dasharray} ${circumference}`}
            strokeDashoffset={s.dashoffset}
            style={{ transform: 'rotate(-90deg)', transformOrigin: '70px 70px' }}
          />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="11" fill="#888">{L("총비용")}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="13" fontWeight="800" fill="#1a1a1a">
          {L("{0}만", fmt(total / 10000))}
        </text>
      </svg>
      <div style={{ flex: 1 }}>
        {slices.filter(s => s.pct > 0).map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: '#555', flex: 1 }}>{s.name}</span>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a1a' }}>
              {(s.pct * 100).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 6개월 막대 차트
function TrendChart({ trendData }) {
  const maxVal = Math.max(...trendData.map(d => Math.max(d.revenue, d.costs, 1)));
  const h = 100;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: `${h + 24}px` }}>
        {trendData.map((d, i) => {
          const rh = maxVal > 0 ? Math.round((d.revenue / maxVal) * h) : 0;
          const ch = maxVal > 0 ? Math.round((d.costs / maxVal) * h) : 0;
          const active = i === trendData.length - 1;
          return (
            <div key={d.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: `${h}px` }}>
                <div title={L("매출: {0}", fmtWon(d.revenue))} style={{
                  width: '14px', height: `${rh}px`, background: active ? '#FF6F0F' : '#E4E4E7',
                  borderRadius: '3px 3px 0 0', transition: 'height 0.4s',
                }} />
                <div title={L("비용: {0}", fmtWon(d.costs))} style={{
                  width: '14px', height: `${ch}px`, background: active ? '#EA580C' : '#fde68a',
                  borderRadius: '3px 3px 0 0', transition: 'height 0.4s',
                }} />
              </div>
              <span style={{ fontSize: '10px', color: active ? '#FF6F0F' : '#9ca3af', fontWeight: active ? '800' : '500' }}>
                {mo(d.month)}
              </span>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '10px', height: '10px', background: '#FF6F0F', borderRadius: '2px' }} />
          <span style={{ fontSize: '11px', color: '#6b7280' }}>{L("매출")}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '10px', height: '10px', background: '#EA580C', borderRadius: '2px' }} />
          <span style={{ fontSize: '11px', color: '#6b7280' }}>{L("비용")}</span>
        </div>
      </div>
    </div>
  );
}

// ── 메인 컴포넌트
export default function ProfitLoss({ profitLoss, onProfitLossChange, orderHistory = [], receivings = [], transactions = [], categories = [], employees = [], canAccess, user, month: monthProp, onMonthChange }) {
  const isMobile = useIsMobile();
  // 부모(OperationsTab)에서 month prop 받으면 그걸 사용해서 lazy 쿼리와 동기화
  const [internalMonth, setInternalMonth] = useState(getCurrentMonth());
  const month = monthProp || internalMonth;
  const setMonth = (m) => {
    setInternalMonth(m);
    if (typeof onMonthChange === 'function') onMonthChange(m);
  };
  const [aiFeedback, setAiFeedback] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  const monthList = useMemo(() => getMonthList(), []);

  // 현재 월 데이터
  const record = useMemo(() => ({
    revenue: { card: 0, cash: 0, delivery: 0 },
    costs: { ingredients: 0, labor: 0, rent: 0, utilities: 0, other: 0 },
    memo: '',
    ...(profitLoss?.[month] || {}),
    revenue: { card: 0, cash: 0, delivery: 0, ...(profitLoss?.[month]?.revenue || {}) },
    costs: { ingredients: 0, labor: 0, rent: 0, utilities: 0, other: 0, ...(profitLoss?.[month]?.costs || {}) },
  }), [profitLoss, month]);

  const updateField = (section, field, value) => {
    onProfitLossChange({
      ...(profitLoss || {}),
      [month]: {
        ...record,
        [section]: { ...record[section], [field]: Number(value) || 0 },
      },
    });
  };

  const updateMemo = (memo) => {
    onProfitLossChange({
      ...(profitLoss || {}),
      [month]: { ...record, memo },
    });
  };

  // 이번 달 수기 매출(카드/현금/배달) 일괄 0으로 초기화
  const resetManualRevenue = () => {
    if (!window.confirm(L("이번 달 수기 입력 매출(카드/현금/배달)을 모두 0으로 비울까요?\n거래장부 자동 집계는 그대로 유지됩니다."))) return;
    onProfitLossChange({
      ...(profitLoss || {}),
      [month]: {
        ...record,
        revenue: { card: 0, cash: 0, delivery: 0 },
      },
    });
  };

  // V158: 식재료비 = 그 달 입고 합계 (실제로 받은 것). 입고 기록이 없는 달만 예전처럼 발주서 합계
  const receivingSum = useMemo(() => monthSummary(receivings, month), [receivings, month]);

  // 발주서 식재료비 자동 연동
  const autoFillIngredients = () => {
    if (receivingSum.count > 0) { updateField('costs', 'ingredients', 0); return; }   // 0 = 입고 합계 자동
    const total = orderHistory
      .filter(h => h.date && h.date.startsWith(month))
      .reduce((sum, h) => sum + (h.totalCost || 0), 0);
    updateField('costs', 'ingredients', total);
  };

  // ── 거래장부 자동 집계 — 결제수단별 매출 (이중합산 X, 자동 채움 패턴) ──
  const ledgerRevByMethod = useMemo(() => {
    const buckets = { card: 0, cash: 0, delivery: 0 };
    for (const t of (transactions || [])) {
      if (t?.type !== 'income') continue;
      if (!t.date?.startsWith(month)) continue;
      // category.sub 우선, 없으면 revenueCategory (POS 임포트는 revenueCategory에 결제수단 저장)
      const sub = String(t?.category?.sub || t?.revenueCategory || '').toLowerCase();
      const amount = (Number(t.supply) || 0) + (Number(t.vat) || 0);
      if (sub === 'sales_card' || sub === 'card') buckets.card += amount;
      else if (sub === 'sales_delivery' || sub === 'delivery') buckets.delivery += amount;
      else buckets.cash += amount;  // sales_cash + 미분류 → 현금
    }
    return buckets;
  }, [transactions, month]);
  const ledgerRevTotal = ledgerRevByMethod.card + ledgerRevByMethod.cash + ledgerRevByMethod.delivery;
  const ledgerCount = useMemo(() => {
    return (transactions || []).filter(t => t?.type === 'income' && t?.date?.startsWith(month)).length;
  }, [transactions, month]);

  // ── 거래장부 자동 집계 — 카테고리별 비용 ──
  const ledgerCosts = useMemo(() => aggregateMonthlyExpenses(transactions, month), [transactions, month]);
  const ledgerCostsTotal = ledgerCosts.ingredients + ledgerCosts.labor + ledgerCosts.rent + ledgerCosts.utilities + ledgerCosts.other;
  const ledgerExpenseCount = useMemo(() => {
    return (transactions || []).filter(t => t?.type === 'expense' && t?.date?.startsWith(month)).length;
  }, [transactions, month]);

  // ── 표시값 = 수기 입력값 > 0 ? 수기값 : 거래장부 자동값 (이중합산 X) ──
  // V171: 인건비 사람별 (profitLoss[월].laborItems)
  const laborItems = Array.isArray(record.laborItems) ? record.laborItems.filter(Boolean) : [];
  const laborSum = laborSummary(laborItems);
  const prevLaborItems = useMemo(() => {
    const [y, mo2] = String(month).split('-').map(Number);
    const pm = `${mo2 === 1 ? y - 1 : y}-${String(mo2 === 1 ? 12 : mo2 - 1).padStart(2, '0')}`;
    const it = profitLoss?.[pm]?.laborItems;
    return Array.isArray(it) ? it.filter(Boolean) : [];
  }, [profitLoss, month]);
  const setLaborItems = (next) => {
    const { laborItems: _drop, ...rest } = record;   // 그 달 기록(메모·매출 등)은 그대로, 사람별 목록만 바꾼다
    const total = laborSummary(next).total;
    onProfitLossChange({
      ...(profitLoss || {}),
      [month]: {
        ...rest,
        costs: { ...record.costs, labor: next.length ? total : 0 },
        ...(next.length ? { laborItems: next } : {}),
      },
    });
  };
  const displayRev = {
    card:     (Number(record.revenue.card) > 0)     ? Number(record.revenue.card)     : ledgerRevByMethod.card,
    cash:     (Number(record.revenue.cash) > 0)     ? Number(record.revenue.cash)     : ledgerRevByMethod.cash,
    delivery: (Number(record.revenue.delivery) > 0) ? Number(record.revenue.delivery) : ledgerRevByMethod.delivery,
  };
  const displayCost = {
    ingredients: (Number(record.costs.ingredients) > 0) ? Number(record.costs.ingredients)
      : (receivingSum.count > 0 ? (receivingSum.byAcct.food || 0) : ledgerCosts.ingredients),
    // V171: 사람별로 입력했으면 그 합계가 인건비
    labor:       laborItems.length ? laborSum.total : (Number(record.costs.labor) > 0) ? Number(record.costs.labor) : ledgerCosts.labor,
    rent:        (Number(record.costs.rent) > 0)        ? Number(record.costs.rent)        : ledgerCosts.rent,
    utilities:   (Number(record.costs.utilities) > 0)   ? Number(record.costs.utilities)   : ledgerCosts.utilities,
    other:       (Number(record.costs.other) > 0)       ? Number(record.costs.other)       : ledgerCosts.other,
  };
  // 각 항목이 수기 오버라이드 상태인지 (UI 라벨용)
  const isOverridden = {
    card:        Number(record.revenue.card) > 0,
    cash:        Number(record.revenue.cash) > 0,
    delivery:    Number(record.revenue.delivery) > 0,
    ingredients: Number(record.costs.ingredients) > 0,
    labor:       Number(record.costs.labor) > 0,
    rent:        Number(record.costs.rent) > 0,
    utilities:   Number(record.costs.utilities) > 0,
    other:       Number(record.costs.other) > 0,
  };
  const anyManualRevenue = isOverridden.card || isOverridden.cash || isOverridden.delivery;
  const anyManualCost = isOverridden.ingredients || isOverridden.labor || isOverridden.rent || isOverridden.utilities || isOverridden.other;

  const totalRevenue = displayRev.card + displayRev.cash + displayRev.delivery;
  // V159: 입고의 식재료 말고 다른 계정과목(주류·음료 · 소모품 · 연료 · 수선·시설 · 비품 · 기타)은 손익에 따로 줄로
  const receivingAcctRows = ACCTS.filter(a => a !== 'food' && Number(receivingSum.byAcct[a]) > 0)
    .map(a => ({ acct: a, label: trLang() === 'zh' ? ACCT_LABEL[a].zh : ACCT_LABEL[a].ko, icon: ACCT_LABEL[a].icon, amount: receivingSum.byAcct[a] }));
  const receivingOtherTotal = receivingAcctRows.reduce((a, r) => a + r.amount, 0);
  // V170: 투자비 감가상각비 (정액법) — 비용에 자동으로
  const inv = useMemo(() => investmentsOf(profitLoss), [profitLoss]);
  const depThisMonth = depForMonth(inv.items, month);
  const totalCosts = displayCost.ingredients + displayCost.labor + displayCost.rent + displayCost.utilities + displayCost.other + receivingOtherTotal + depThisMonth;
  const netProfit = totalRevenue - totalCosts;
  const ingRatio = totalRevenue > 0 ? (displayCost.ingredients / totalRevenue * 100).toFixed(1) : null;
  // V166: 음식 매출 vs 주류·음료 매출 (거래장부 기준) · 원가율은 식재료 입고/음식 매출, 주류·음료 입고/주류·음료 매출
  const classByNorm = useMemo(() => menuClassMap(categories), [categories]);
  const revMix = useMemo(() => monthRevenueMix(transactions, month, classByNorm), [transactions, month, classByNorm]);
  const bevCost = Number(receivingSum.byAcct.drink) || 0;
  const foodCostRatio = revMix.food > 0 ? (displayCost.ingredients / revMix.food * 100).toFixed(1) : null;
  const bevCostRatio = revMix.bev > 0 ? (bevCost / revMix.bev * 100).toFixed(1) : null;
  const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue * 100).toFixed(1) : null;

  // 6개월 추이 — 수기 > 0 ? 수기 : 거래장부 (카테고리별)
  // V170: 월 계산을 함수로 떼어 누적 손익·투자 회수에도 쓴다. 감가상각비 포함
  const figuresOf = useMemo(() => {
    // 월별 거래장부 자동 합계 미리 계산
    const monthlyLedgerRev = {};
    const monthlyLedgerCost = {};
    for (const t of (transactions || [])) {
      const m = (t?.date || '').slice(0, 7);
      if (!m) continue;
      const amount = (Number(t.supply) || 0) + (Number(t.vat) || 0);
      if (t?.type === 'income') monthlyLedgerRev[m] = (monthlyLedgerRev[m] || 0) + amount;
      else if (t?.type === 'expense') monthlyLedgerCost[m] = (monthlyLedgerCost[m] || 0) + amount;
    }
    return (m) => {
      const dep = depForMonth(inv.items, m);
      if (m === month) return { month: m, revenue: totalRevenue, costs: totalCosts, profit: totalRevenue - totalCosts, dep };   // 이달은 화면 값 그대로
      const r = profitLoss?.[m] || {};
      const mr = r.revenue || {};
      const mc = r.costs || {};
      const card = Number(mr.card) > 0 ? Number(mr.card) : 0;
      const cash = Number(mr.cash) > 0 ? Number(mr.cash) : 0;
      const deli = Number(mr.delivery) > 0 ? Number(mr.delivery) : 0;
      const manualRevSum = card + cash + deli;
      // 매출은 단순화: 수기 합계가 있으면 수기, 아니면 거래장부
      const revenue = manualRevSum > 0 ? manualRevSum : (monthlyLedgerRev[m] || 0);
      const manualCostSum = (Number(mc.ingredients) || 0) + (Number(mc.labor) || 0)
        + (Number(mc.rent) || 0) + (Number(mc.utilities) || 0) + (Number(mc.other) || 0);
      // V160: 입고도 비용에 (식재료비를 손으로 고친 달은 입고 식재료는 빼고)
      const rs = monthSummary(receivings, m);
      const rFood = Number(rs.byAcct?.food) || 0;
      const rOther = Math.max(0, (Number(rs.total) || 0) - rFood);
      const cost = (manualCostSum > 0 ? manualCostSum : (monthlyLedgerCost[m] || 0)) + (Number(mc.ingredients) > 0 ? 0 : rFood) + rOther + dep;
      return { month: m, revenue, costs: cost, profit: revenue - cost, dep };
    };
  }, [profitLoss, transactions, receivings, month, totalCosts, totalRevenue, inv]);
  const trendData = useMemo(() => getMonthList().slice(0, 6).reverse().map(figuresOf), [figuresOf]);

  // V170: 누적 손익 · 투자 회수 — 기준 월(오픈 월)부터 보고 있는 달까지 (최대 60개월)
  const pay = useMemo(() => {
    const start = startMonthOf(inv);
    const months = [];
    if (start && monthDiff(start, month) >= 0) {
      const n = Math.min(60, monthDiff(start, month) + 1);
      for (let i = n - 1; i >= 0; i--) months.push(addMonths(month, -i));
    }
    return paybackSummary(inv.items, months.map(figuresOf), month);
  }, [inv, month, figuresOf]);
  const setInvestments = (next) => onProfitLossChange({ ...(profitLoss || {}), _investments: next });

  // ── 손익계산서 PDF 출력 (브라우저 인쇄 API 활용)
  const handlePrintPDF = () => {
    if (totalRevenue === 0 && totalCosts === 0) {
      alert(L("매출 또는 비용을 먼저 입력해주세요."));
      return;
    }
    let bizInfo = {};
    try {
      bizInfo = JSON.parse(localStorage.getItem('ddabak_biz_info') || '{}');
    } catch (e) { bizInfo = {}; }

    const monthLabel = ym(month);
    const today = new Date();
    const printDate = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

    const row = (label, value, opts = {}) => `
      <tr ${opts.bold ? 'class="bold"' : ''} ${opts.highlight ? 'class="highlight"' : ''} ${opts.sub ? 'class="sub"' : ''}>
        <td>${label}</td>
        <td class="num">${typeof value === 'number' ? fmtWon(value) : value}</td>
      </tr>`;

    const html = LH(`<!DOCTYPE html>
<html lang="ko"><head><meta charset="utf-8" />
<title>손익계산서_${month}_${bizInfo.name || '셈핏'}</title>
<style>
  @page { size: A4; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Malgun Gothic', '맑은 고딕', -apple-system, BlinkMacSystemFont, sans-serif; color: #1a1a1a; margin: 0; padding: 24px; background: #fff; }
  .header { border-bottom: 3px solid #FF6F0F; padding-bottom: 14px; margin-bottom: 22px; }
  .title { font-size: 26px; font-weight: 900; color: #FF6F0F; margin: 0; }
  .sub-title { font-size: 13px; color: #6b7280; margin-top: 6px; }
  .biz-info { display: grid; grid-template-columns: 110px 1fr 110px 1fr; gap: 6px 14px; font-size: 12px; padding: 14px 18px; background: #f9fafb; border-radius: 8px; margin-bottom: 22px; }
  .biz-info .lbl { color: #6b7280; font-weight: 700; }
  .biz-info .val { color: #1a1a1a; }
  h2 { font-size: 15px; color: #FF6F0F; margin: 22px 0 10px 0; padding-bottom: 6px; border-bottom: 1.5px solid #e5e7eb; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  td { padding: 9px 12px; border-bottom: 1px solid #f0f0f0; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }
  tr.bold td { font-weight: 800; background: #f8fafc; border-bottom: 2px solid #d1d5db; }
  tr.sub td { color: #6b7280; padding-left: 24px; font-size: 12px; }
  tr.highlight td { background: ${isProfit ? '#FAFAFA' : '#fef2f2'}; color: ${profitColor}; font-weight: 900; font-size: 15px; border-top: 2px solid ${profitColor}; border-bottom: 2px solid ${profitColor}; }
  .summary-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 22px; }
  .summary-card { background: #f9fafb; border-radius: 8px; padding: 14px; text-align: center; border: 1px solid #e5e7eb; }
  .summary-card .lbl { font-size: 11px; color: #6b7280; margin-bottom: 6px; font-weight: 700; }
  .summary-card .val { font-size: 18px; font-weight: 900; }
  .summary-card.profit .val { color: ${profitColor}; }
  .memo-box { padding: 12px 14px; background: #FAFAFA; border-left: 3px solid #EA580C; font-size: 12px; line-height: 1.7; color: #78350f; white-space: pre-wrap; margin-top: 8px; }
  .footer { margin-top: 32px; padding-top: 14px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; display: flex; justify-content: space-between; }
  .stamp { font-size: 10px; color: #9ca3af; margin-top: 4px; }
  @media print {
    body { padding: 0; }
    .no-print { display: none !important; }
  }
  .actions { position: fixed; top: 16px; right: 16px; z-index: 999; }
  .actions button { padding: 10px 18px; background: #FF6F0F; color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; margin-left: 6px; box-shadow: 0 3px 12px rgba(0,0,0,0.18); }
  .actions button.cancel { background: #6b7280; }
</style>
</head><body>
  <div class="actions no-print">
    <button onclick="window.print()">🖨️ 인쇄 / PDF 저장</button>
    <button class="cancel" onclick="window.close()">닫기</button>
  </div>

  <div class="header">
    <h1 class="title">월별 손익계산서</h1>
    <div class="sub-title">${monthLabel} · 작성일 ${printDate}</div>
  </div>

  <div class="biz-info">
    <div class="lbl">상호명</div><div class="val">${bizInfo.name || '—'}</div>
    <div class="lbl">사업자번호</div><div class="val">${bizInfo.regNum || '—'}</div>
    <div class="lbl">대표자명</div><div class="val">${bizInfo.ceo || '—'}</div>
    <div class="lbl">연락처</div><div class="val">${bizInfo.phone || '—'}</div>
    <div class="lbl">사업장주소</div><div class="val" style="grid-column: span 3;">${bizInfo.address || '—'}</div>
  </div>

  <div class="summary-grid">
    <div class="summary-card"><div class="lbl">총 매출</div><div class="val">${fmtWon(totalRevenue)}</div></div>
    <div class="summary-card"><div class="lbl">총 비용</div><div class="val">${fmtWon(totalCosts)}</div></div>
    <div class="summary-card profit"><div class="lbl">${isProfit ? '순이익' : '순손실'}${profitMargin ? ' (' + profitMargin + '%)' : ''}</div><div class="val">${fmtWon(netProfit)}</div></div>
  </div>

  <h2>1. 매출 내역</h2>
  <table>
    ${row(L('카드 매출'), record.revenue.card || 0, { sub: true })}
    ${row(L('현금 매출'), record.revenue.cash || 0, { sub: true })}
    ${row(L('배달 매출'), record.revenue.delivery || 0, { sub: true })}
    ${row(L('매출 합계'), totalRevenue, { bold: true })}
  </table>

  <h2>2. 비용 내역</h2>
  <table>
    ${row(L('식재료비'), displayCost.ingredients || 0, { sub: true })}
    ${receivingAcctRows.map(r => row(r.label + L(' (입고)'), r.amount, { sub: true })).join('')}
    ${row(L('인건비'), displayCost.labor || 0, { sub: true })}
    ${laborItems.map(it => row('　· ' + (it.name || '') , (Number(it.base) || 0) + (Number(it.allowance) || 0) + (Number(it.insurance) || 0) + (Number(it.other) || 0), { sub: true })).join('')}
    ${row(L('임대료'), displayCost.rent || 0, { sub: true })}
    ${row(L('공과금'), displayCost.utilities || 0, { sub: true })}
    ${row(L('기타 경비'), displayCost.other || 0, { sub: true })}
    ${depThisMonth > 0 ? row(L('감가상각비'), depThisMonth, { sub: true }) : ''}
    ${row(L('비용 합계'), totalCosts, { bold: true })}
  </table>

  <h2>3. 손익 분석</h2>
  <table>
    ${row(L('총 매출'), totalRevenue)}
    ${row(L('(−) 총 비용'), totalCosts)}
    ${row(L(isProfit ? '순이익' : '순손실'), netProfit, { highlight: true })}
    ${ingRatio ? row(L('식재료 원가율'), ingRatio + '%', { sub: true }) : ''}
    ${profitMargin ? row(L(isProfit ? '순이익률' : '손실률'), profitMargin + '%', { sub: true }) : ''}
    ${pay.cumMargin != null ? row(L('누적 순이익률 ({0}개월)', pay.months), pay.cumMargin + '%', { sub: true }) : ''}
    ${pay.rate != null ? row(L('투자 회수율'), pay.rate + '%' + (pay.expected ? ' · ' + L('예상 회수 {0}', ym(pay.expected)) : ''), { sub: true }) : ''}
  </table>

  ${record.memo ? LH(`<h2>4. 이달 메모</h2><div class="memo-box">${(record.memo || '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>`) : ''}

  <div class="footer">
    <span>본 문서는 자동 생성된 내부 손익자료이며, 정식 회계장부가 아닙니다.</span>
    <span>Powered by 셈핏</span>
  </div>
  <div class="stamp">출력일: ${printDate} · 셈핏 (semfit.app)</div>
</body></html>`);

    const win = window.open('', '_blank', 'width=860,height=1000');
    if (!win) {
      alert(L("팝업 차단을 해제해주세요."));
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  // AI 피드백
  const fetchAiFeedback = async () => {
    if (totalRevenue === 0 && totalCosts === 0) {
      setAiError(L("매출과 비용을 먼저 입력해주세요."));
      return;
    }
    setAiLoading(true);
    setAiFeedback('');
    setAiError('');
    try {
      const res = await fetch('/api/ai-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          month, totalRevenue, totalCosts, netProfit,
          costRatio: ingRatio, profitMargin,
          costs: { ...displayCost, ...Object.fromEntries(receivingAcctRows.map(r => [r.acct, r.amount])), depreciation: depThisMonth }, revenue: displayRev,
          // V170 진단: 통화 · 화면 언어 · 음식/주류·음료 · 투자비·감가상각·회수·누적 순이익률
          currency: MU(), lang: trLang(),
          mix: { food: revMix.food, bev: revMix.bev, foodCostRatio, bevCostRatio },
          labor: laborItems.length ? laborSum : null,   // V171 인건비 사람별
          investment: inv.items.length ? {
            total: investTotals(inv.items).total, deposit: investTotals(inv.items).deposit, monthlyDep: depThisMonth,
            cumMonths: pay.months, cumRevenue: pay.cumRevenue, cumProfit: pay.cumProfit, cumMargin: pay.cumMargin,
            paybackRate: pay.rate, recoveredAt: pay.recoveredAt, expected: pay.expected, monthsLeft: pay.monthsLeft, avgCash: pay.avgCash,
          } : null,
        }),
      });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      setAiFeedback(data.feedback || L("피드백을 가져오지 못했어요."));
    } catch (e) {
      setAiError(L("AI 피드백 요청에 실패했어요. 잠시 후 다시 시도해주세요."));
    }
    setAiLoading(false);
  };

  // ── 프로 플랜 게이팅 (어드민은 무조건 통과)
  if (!canAccess('pro') && !isAdmin(user)) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '14px', padding: '40px', textAlign: 'center',
      }}>
        <div style={{ fontSize: '52px' }}>💳</div>
        <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1a1a1a' }}>
          {L("매장 손익 관리")}
        </h2>
        <p style={{ color: '#6b7280', lineHeight: 1.7, maxWidth: '320px' }}>
          {L("이 기능은")} <strong>{L("평생 Pro")}</strong> {L("전용이에요.")}<br />
          {L("이용권 한 번 결제로 손익 대시보드 · 월별 비교 · AI 경영 피드백을 평생 사용해보세요.")}
        </p>
        <div style={{
          background: '#F4F4F5', borderRadius: '12px', padding: '16px 24px',
          color: '#52525B', fontSize: '13px', maxWidth: '320px',
        }}>
          ✨ <strong>{L("프로 플랜 혜택:")}</strong><br />
          {L("매장 손익 관리 · 브랜딩 마법사 · 업체 운영 메뉴얼 · AI 피드백")}
        </div>
      </div>
    );
  }

  const isProfit = netProfit >= 0;
  const profitColor = isProfit ? '#FF6F0F' : '#52525B';

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5', padding: isMobile ? '12px' : '20px' }}>

      {/* ── 월 선택 */}
      <div style={{
        background: '#fff', borderRadius: '14px', padding: '14px 18px',
        marginBottom: '16px', display: 'flex', alignItems: 'center',
        gap: '12px', flexWrap: 'wrap', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <span style={{ fontWeight: '800', fontSize: '15px', color: '#1a1a1a', flexShrink: 0 }}>{L("📅 월 선택")}</span>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {monthList.slice(0, isMobile ? 4 : 6).map(m => (
            <button key={m} onClick={() => { setMonth(m); setAiFeedback(''); setAiError(''); }}
              style={{
                padding: '6px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: '700', transition: 'all 0.15s',
                background: m === month ? '#FF6F0F' : '#f3f4f6',
                color: m === month ? '#fff' : '#555',
              }}>
              {m.slice(0, 4) === getCurrentMonth().slice(0, 4) ? mo(m) : ym(m)}
            </button>
          ))}
        </div>
        <select
          value={month}
          onChange={e => { setMonth(e.target.value); setAiFeedback(''); setAiError(''); }}
          style={{
            padding: '7px 10px', borderRadius: '8px', border: '1.5px solid #e5e7eb',
            fontSize: '13px', background: '#fff', cursor: 'pointer',
          }}>
          {monthList.map(m => (
            <option key={m} value={m}>{ym(m)}</option>
          ))}
        </select>

        <button
          onClick={handlePrintPDF}
          title={L("이달 손익계산서를 PDF로 출력합니다")}
          style={{
            marginLeft: 'auto',
            padding: '8px 14px', borderRadius: '8px',
            border: 'none', background: '#EA580C',
            color: '#fff', fontSize: '13px', fontWeight: '800', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px',
            boxShadow: '0 2px 8px rgba(30,107,60,0.25)',
          }}>
          {L("📄 PDF 출력")}
        </button>
      </div>

      {/* ── 요약 카드 */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <SummaryCard
          label={L("총 매출")}
          value={fmtWon(totalRevenue)}
          sub={ledgerCount > 0
            ? L("거래장부 {0}건 자동{1}", ledgerCount, anyManualRevenue ? L(" (일부 수기 수정)") : '')
            : L("카드+현금+배달")}
        />
        <SummaryCard
          label={L("총 비용")}
          value={fmtWon(totalCosts)}
          sub={ledgerExpenseCount > 0
            ? L("거래장부 {0}건 자동{1}", ledgerExpenseCount, anyManualCost ? L(" (일부 수기 수정)") : '')
            : L("식재료+인건비+기타")}
        />
        <SummaryCard
          label={L("순이익")}
          value={fmtWon(netProfit)}
          sub={profitMargin ? L("이익률 {0}%", profitMargin) : ''}
          color={isProfit ? '#FF6F0F' : '#52525B'}
          big
        />
        <SummaryCard
          label={L("식재료 원가율")}
          value={ingRatio ? `${ingRatio}%` : '—'}
          sub={ingRatio ? (Number(ingRatio) < 35 ? L("✅ 양호") : Number(ingRatio) < 45 ? L("⚠️ 주의") : L("🚨 위험")) : ''}
        />
      </div>

      {/* ── 메인 레이아웃 */}
      <div style={{ display: 'flex', gap: '16px', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>

        {/* ── 왼쪽: 입력 패널 */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* 매출 입력 */}
          <SectionCard title={L("매출 입력")} icon="💰" color="#FF6F0F">
            {ledgerCount > 0 && (
              <div style={{
                padding: '10px 12px', marginBottom: 10,
                background: "#FAFAFA", borderRadius: 8, border: '1px solid #F4F4F5',
                fontSize: 12, color: '#9A3412', lineHeight: 1.5,
              }}>
                {L("📊 거래장부에서 자동 채워졌어요 ({0}건 · 합계 {1})", ledgerCount, fmtWon(ledgerRevTotal))}
                <div style={{ marginTop: 4, fontSize: 11, color: '#047857' }}>
                  {L("값이 다르면 입력란을 직접 수정하세요.")} <strong>{L("0으로 두면 거래장부 값이 자동 적용")}</strong>{L("됩니다.")}
                </div>
              </div>
            )}
            {anyManualRevenue && (
              <button onClick={resetManualRevenue} style={{
                width: '100%', padding: '8px 12px', marginBottom: 10,
                background: '#fff', border: '1px solid #d1d5db',
                borderRadius: 8, color: '#6b7280', fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
              }}>
                {L("↺ 수기 수정값 초기화 → 거래장부 자동값으로 복귀")}
              </button>
            )}
            <InputRow label={L("카드{0}", isOverridden.card ? L(" ✏️ 수정됨") : (ledgerRevByMethod.card > 0 ? L(" · 장부 자동") : ''))}
              value={displayRev.card}
              onChange={v => updateField('revenue', 'card', v)} />
            <InputRow label={L("현금{0}", isOverridden.cash ? L(" ✏️ 수정됨") : (ledgerRevByMethod.cash > 0 ? L(" · 장부 자동") : ''))}
              value={displayRev.cash}
              onChange={v => updateField('revenue', 'cash', v)} />
            <InputRow label={L("배달{0}", isOverridden.delivery ? L(" ✏️ 수정됨") : (ledgerRevByMethod.delivery > 0 ? L(" · 장부 자동") : ''))}
              value={displayRev.delivery}
              onChange={v => updateField('revenue', 'delivery', v)} />
            <div style={{
              marginTop: '8px', padding: '10px 12px', background: '#FAFAFA',
              borderRadius: '8px',
              display: 'flex', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#18181B' }}>{L("총 매출")}</span>
              <span style={{ fontSize: '15px', fontWeight: '900', color: '#18181B' }}>{fmtWon(totalRevenue)}</span>
            </div>
            {/* V166 음식 · 주류·음료 */}
            {(revMix.bev > 0 || bevCost > 0) && (
              <div data-pl-mix style={{ marginTop: 8, padding: '10px 12px', background: '#F8FAFC', borderRadius: 8, fontSize: 12, color: '#3f3f46', lineHeight: 1.7 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>🍽️ {L("음식 매출")}</span>
                  <span><b>{fmtWon(revMix.food)}</b>{foodCostRatio && <span style={{ color: '#6b7280' }}> · {L("식재료 원가율 {0}%", foodCostRatio)}</span>}</span>
                </div>
                <div data-pl-mix-bev style={{ display: 'flex', justifyContent: 'space-between', color: '#1D4ED8' }}>
                  <span>🍺🥤 {L("주류·음료 매출")}</span>
                  <span><b>{fmtWon(revMix.bev)}</b>{bevCostRatio && <span style={{ color: '#6b7280' }}> · {L("주류·음료 원가율 {0}%", bevCostRatio)}</span>}</span>
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  {L("거래장부 매출 기준 · 주류·음료 입고 {0}", fmtWon(bevCost))}{revMix.unknown > 0 ? L(" · 구분 없는 매출 {0}은 음식으로", fmtWon(revMix.unknown)) : ''}
                </div>
              </div>
            )}
          </SectionCard>

          {/* 비용 입력 */}
          <SectionCard title={L("비용 입력")} icon="📦" color="#EA580C">
            {ledgerExpenseCount > 0 && (
              <div style={{
                padding: '10px 12px', marginBottom: 10,
                background: "#FAFAFA", borderRadius: 8, border: '1px solid #F4F4F5',
                fontSize: 12, color: '#9A3412', lineHeight: 1.5,
              }}>
                {L("📊 거래장부에서 자동 채워졌어요 ({0}건 · 합계 {1})", ledgerExpenseCount, fmtWon(ledgerCostsTotal))}
                <div style={{ marginTop: 4, fontSize: 11, color: '#047857' }}>
                  {L("값이 다르면 입력란을 직접 수정하세요.")} <strong>{L("0으로 두면 거래장부 값이 자동 적용")}</strong>{L("됩니다.")}
                </div>
              </div>
            )}
            <InputRow label={L("식재료비{0}", isOverridden.ingredients ? L(" ✏️ 수정됨") : (receivingSum.count > 0 ? L(" · 입고 자동") : (ledgerCosts.ingredients > 0 ? L(" · 장부 자동") : '')))}
              value={displayCost.ingredients}
              onChange={v => updateField('costs', 'ingredients', v)}
              autoFill={autoFillIngredients}
              autoLabel={receivingSum.count > 0 ? L("입고↔연동") : L("발주↔연동")}
              autoTitle={receivingSum.count > 0 ? L("입고 합계로 되돌리기") : L("발주서에서 자동 계산")} />
            {receivingSum.count > 0 && (
              <div data-pl-receiving style={{ margin: '-4px 0 12px 90px', fontSize: 11, color: '#9A3412', lineHeight: 1.5 }}>
                {L("📥 이달 입고 {0}건 · 합계 {1}", receivingSum.count, fmtWon(receivingSum.total))}{receivingAcctRows.length > 0 ? L(" (식재료 {0})", fmtWon(receivingSum.byAcct.food || 0)) : ''}
                {isOverridden.ingredients ? L(" — 직접 고친 값을 쓰는 중 (입고↔연동 누르면 입고 식재료 합계로)") : L(" — 식재료는 식재료비에, 다른 계정과목은 아래 줄에 자동으로 들어가요")}
                {receivingSum.byVendor.length > 0 && (
                  <div style={{ color: '#6b7280' }}>{receivingSum.byVendor.slice(0, 5).map(v => `${trLang() === 'zh' && v.vendor_zh ? v.vendor_zh : v.vendor} ${fmtWon(v.total)}`).join(' · ')}{receivingSum.byVendor.length > 5 ? ' …' : ''}</div>
                )}
              </div>
            )}
            {receivingAcctRows.map(r => (
              <div key={r.acct} data-pl-acct={r.acct} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ width: '80px', fontSize: '13px', color: '#555', flexShrink: 0 }}>{r.icon} {r.label}</span>
                <div style={{ flex: 1, padding: '9px 12px', borderRadius: 8, background: '#FAFAFA', border: '1px dashed #E4E4E7', fontSize: 14, fontWeight: 700, textAlign: 'right' }}>{fmtWon(r.amount)}</div>
                <span style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0, width: 62 }}>{L("📥 입고")}</span>
              </div>
            ))}
            {depThisMonth > 0 && (
              <div data-pl-dep style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ width: '80px', fontSize: '13px', color: '#555', flexShrink: 0 }}>🏗️ {L("감가상각비")}</span>
                <div style={{ flex: 1, padding: '9px 12px', borderRadius: 8, background: '#FAFAFA', border: '1px dashed #E4E4E7', fontSize: 14, fontWeight: 700, textAlign: 'right' }}>{fmtWon(depThisMonth)}</div>
                <span style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0, width: 62 }}>{L("투자비 자동")}</span>
              </div>
            )}
            {laborItems.length ? (
              <div data-pl-labor-row style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ width: '80px', fontSize: '13px', color: '#555', flexShrink: 0 }}>{L("인건비 · 👥 {0}명", laborSum.people)}</span>
                <div style={{ flex: 1, padding: '9px 12px', borderRadius: 8, background: '#FAFAFA', border: '1px dashed #E4E4E7', fontSize: 14, fontWeight: 700, textAlign: 'right' }}>{fmtWon(laborSum.total)}</div>
              </div>
            ) : (
              <InputRow label={L("인건비{0}", isOverridden.labor ? L(" ✏️ 수정됨") : (ledgerCosts.labor > 0 ? L(" · 장부 자동") : ''))}
                value={displayCost.labor}
                onChange={v => updateField('costs', 'labor', v)} />
            )}
            <LaborDetail items={laborItems} prevItems={prevLaborItems} employees={employees} onChange={setLaborItems} isMobile={isMobile} money={fmtWon} />
            <InputRow label={L("임대료{0}", isOverridden.rent ? L(" ✏️ 수정됨") : (ledgerCosts.rent > 0 ? L(" · 장부 자동") : ''))}
              value={displayCost.rent}
              onChange={v => updateField('costs', 'rent', v)} />
            <InputRow label={L("공과금{0}", isOverridden.utilities ? L(" ✏️ 수정됨") : (ledgerCosts.utilities > 0 ? L(" · 장부 자동") : ''))}
              value={displayCost.utilities}
              onChange={v => updateField('costs', 'utilities', v)} />
            <InputRow label={L("기타{0}", isOverridden.other ? L(" ✏️ 수정됨") : (ledgerCosts.other > 0 ? L(" · 장부 자동") : ''))}
              value={displayCost.other}
              onChange={v => updateField('costs', 'other', v)} />
            <div style={{
              marginTop: '8px', padding: '10px 12px', background: '#FAFAFA',
              borderRadius: '8px',
              display: 'flex', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#EA580C' }}>{L("총 비용")}</span>
              <span style={{ fontSize: '15px', fontWeight: '900', color: '#EA580C' }}>{fmtWon(totalCosts)}</span>
            </div>
          </SectionCard>

          {/* V170 투자비 · 감가상각 · 투자 회수 · 누적 순이익률 */}
          <SectionCard title={L("투자비 · 감가상각 · 투자 회수")} icon="🏗️" color="#0F766E">
            <InvestmentPanel inv={inv} month={month} depThisMonth={depThisMonth} pay={pay} onChange={setInvestments} money={fmtWon} />
          </SectionCard>

          {/* 메모 */}
          <div style={{ background: '#fff', borderRadius: '14px', padding: '14px 18px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <div style={{ fontWeight: '700', fontSize: '13px', color: '#555', marginBottom: '8px' }}>{L("📝 이달 메모")}</div>
            <textarea
              value={record.memo}
              onChange={e => updateMemo(e.target.value)}
              placeholder={L("이달 특이사항, 주요 이벤트 등 자유롭게 적어보세요")}
              style={{
                width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
                borderRadius: '8px', fontSize: '13px', resize: 'vertical', minHeight: '70px',
                outline: 'none', lineHeight: 1.6,
              }}
            />
          </div>
        </div>

        {/* ── 오른쪽: 분석 패널 */}
        <div style={{ flex: isMobile ? 'none' : '0 0 340px', width: isMobile ? '100%' : '340px' }}>

          {/* 비용 구성 */}
          <SectionCard title={L("비용 구성")} icon="🍩" color="#7C3AED">
            <CostDonut costs={displayCost} extra={depThisMonth > 0 ? [...receivingAcctRows, { acct: 'depreciation', label: L('감가상각비'), icon: '🏗️', amount: depThisMonth }] : receivingAcctRows} />
          </SectionCard>

          {/* 6개월 추이 */}
          <SectionCard title={L("6개월 추이")} icon="📈" color="#1A56A0">
            <TrendChart trendData={trendData} />
            <div style={{ marginTop: '14px', borderTop: '1px solid #f0f0f0', paddingTop: '10px' }}>
              {trendData.filter(d => d.revenue > 0 || d.costs > 0).length === 0 ? (
                <p style={{ color: '#bbb', fontSize: '12px', textAlign: 'center' }}>{L("데이터를 입력하면 추이가 보여요")}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {trendData.slice(-3).reverse().map(d => (
                    <div key={d.month} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                      <span style={{ color: '#888' }}>{ym(d.month)}</span>
                      <span style={{ fontWeight: '700', color: d.profit >= 0 ? '#FF6F0F' : '#52525B' }}>
                        {d.profit >= 0 ? '+' : ''}{fmtWon(d.profit)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>

          {/* AI 피드백 */}
          <div style={{
            background: '#fff', borderRadius: '14px', overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
          }}>
            <div style={{
              padding: '14px 18px', borderBottom: '1px solid #f0f0f0',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <span style={{ fontSize: '18px' }}>🤖</span>
              <span style={{ fontWeight: '800', fontSize: '15px', color: '#374151' }}>{L("AI 경영 피드백")}</span>
              <span style={{
                marginLeft: 'auto', fontSize: '10px', background: '#F4F4F5',
                color: '#52525B', padding: '2px 8px', borderRadius: '10px', fontWeight: '700',
              }}>PRO</span>
            </div>
            <div style={{ padding: '16px 18px' }}>
              {aiFeedback ? (
                <div>
                  <div style={{
                    background: '#f8fafc', borderRadius: '10px', padding: '14px',
                    fontSize: '13px', lineHeight: 1.8, color: '#374151', whiteSpace: 'pre-wrap',
                  }}>
                    {aiFeedback}
                  </div>
                  <button
                    onClick={() => { setAiFeedback(''); setAiError(''); }}
                    style={{
                      marginTop: '10px', padding: '7px 14px', borderRadius: '8px',
                      border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280',
                      fontSize: '12px', cursor: 'pointer', fontWeight: '600',
                    }}>
                    {L("다시 분석하기")}
                  </button>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', lineHeight: 1.7 }}>
                    {L("이달 손익 데이터를 AI가 분석하고 구체적인 경영 개선 조언을 드려요.")}
                  </p>
                  {aiError && (
                    <div style={{
                      background: '#F4F4F5', color: '#52525B', padding: '10px 12px',
                      borderRadius: '8px', fontSize: '12px', marginBottom: '10px',
                    }}>
                      {aiError}
                    </div>
                  )}
                  <button
                    onClick={fetchAiFeedback}
                    disabled={aiLoading}
                    style={{
                      width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                      background: aiLoading ? '#9ca3af' : '#27272A',
                      color: '#fff', fontSize: '14px', fontWeight: '800', cursor: aiLoading ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    }}>
                    {aiLoading ? (
                      <>
                        <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                        {L("AI 분석 중...")}
                      </>
                    ) : L("✨ AI 피드백 받기")}
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
